/**
 * Format Selection Step (Story 5.23, Task 3)
 *
 * First step in the report generation flow. Displays two format cards:
 * - Document Report (A4 portrait PDF)
 * - Slide Deck (16:9 landscape presentation)
 *
 * Document is pre-selected by default for power-user speed.
 */
import { useState } from "react";
import { Check } from "lucide-react";
import { ArrowRight } from "@phosphor-icons/react";
import type { ReportFormat } from "@provenleap/types/report-rendering";

export interface FormatSelectionStepProps {
  defaultFormat?: ReportFormat;
  onSelect: (format: ReportFormat) => void;
}

export function FormatSelectionStep({
  defaultFormat = "document",
  onSelect,
}: FormatSelectionStepProps) {
  const [selected, setSelected] = useState<ReportFormat>(defaultFormat);

  const handleContinue = () => {
    onSelect(selected);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-[21px] font-semibold tracking-tight">
          Choose Report Type
        </h2>
        <p className="text-[15px] text-foreground/70 mt-1">
          Select the format that best fits your delivery method
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Document Report Card */}
        <button
          type="button"
          onClick={() => setSelected("document")}
          className="relative flex flex-col items-center p-6 rounded-3xl bg-background shadow-xs transition-all duration-200 text-left cursor-pointer hover:-translate-y-0.5 hover:shadow-sm"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="format-card-document"
        >
          {selected === "document" && (
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}

          {/* Mockup area — fixed height so text aligns across both cards */}
          <div className="h-44 mb-4 flex items-center justify-center">
            {/* A4 Portrait Mockup */}
            <div className="w-32 h-44 rounded-lg bg-background p-3 flex flex-col gap-2" style={{ border: "0.5px solid var(--hairline)", boxShadow: "0 2px 4px rgba(0,0,0,0.06)" }}>
            {/* Header: logo dot + title line */}
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded-full bg-primary/40 shrink-0" />
              <div className="h-2 w-full rounded-sm bg-muted-foreground/25" />
            </div>
            {/* KPI row */}
            <div className="flex gap-1">
              <div className="flex-1 h-6 rounded bg-primary/10 flex flex-col items-center justify-center gap-0.5">
                <div className="h-1.5 w-5 rounded-sm bg-primary/40" />
                <div className="h-1 w-3 rounded-sm bg-primary/25" />
              </div>
              <div className="flex-1 h-6 rounded bg-primary/10 flex flex-col items-center justify-center gap-0.5">
                <div className="h-1.5 w-5 rounded-sm bg-primary/40" />
                <div className="h-1 w-3 rounded-sm bg-primary/25" />
              </div>
              <div className="flex-1 h-6 rounded bg-primary/10 flex flex-col items-center justify-center gap-0.5">
                <div className="h-1.5 w-5 rounded-sm bg-primary/40" />
                <div className="h-1 w-3 rounded-sm bg-primary/25" />
              </div>
            </div>
            {/* Bar chart area */}
            <div className="flex-1 flex items-end gap-0.5 px-1">
              <div className="w-full h-[60%] rounded-t-sm bg-primary/30" />
              <div className="w-full h-[85%] rounded-t-sm bg-primary/45" />
              <div className="w-full h-[45%] rounded-t-sm bg-primary/30" />
              <div className="w-full h-[70%] rounded-t-sm bg-primary/45" />
              <div className="w-full h-[55%] rounded-t-sm bg-primary/30" />
            </div>
            {/* Text lines */}
            <div className="flex flex-col gap-0.5">
              <div className="h-1.5 w-full rounded-sm bg-muted-foreground/20" />
              <div className="h-1.5 w-3/4 rounded-sm bg-muted-foreground/15" />
            </div>
            </div>
          </div>

          <h3 className="text-base font-semibold">Document Report</h3>
          <p className="text-sm text-foreground/70 text-center mt-1">
            Send as PDF for clients to read on their own time
          </p>
        </button>

        {/* Slide Deck Card */}
        <button
          type="button"
          onClick={() => setSelected("slide-deck")}
          className="relative flex flex-col items-center p-6 rounded-3xl bg-background shadow-xs transition-all duration-200 text-left cursor-pointer hover:-translate-y-0.5 hover:shadow-sm"
          style={{ border: "0.5px solid var(--hairline)" }}
          data-testid="format-card-slide-deck"
        >
          {selected === "slide-deck" && (
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}

          {/* Mockup area — fixed height so text aligns across both cards */}
          <div className="h-44 mb-4 flex items-center justify-center">
            {/* 16:9 Landscape Mockup */}
            <div className="w-44 h-[100px] rounded-lg bg-background p-3 flex flex-col gap-2" style={{ border: "0.5px solid var(--hairline)", boxShadow: "0 2px 4px rgba(0,0,0,0.06)" }}>
            {/* Header: logo dot + title */}
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-primary/40 shrink-0" />
              <div className="h-2 w-1/2 rounded-sm bg-muted-foreground/25" />
            </div>
            {/* Content: pie chart + bullet points */}
            <div className="flex gap-3 flex-1">
              {/* Pie chart — brand-red at varying opacities so we never use non-brand colors */}
              <div
                className="w-11 h-11 rounded-full shrink-0 self-center"
                style={{
                  background:
                    "conic-gradient(rgba(255,109,111,0.55) 0deg 140deg, rgba(255,109,111,0.35) 140deg 250deg, rgba(255,109,111,0.2) 250deg 360deg)",
                }}
              />
              {/* Bullet lines */}
              <div className="flex flex-col justify-center gap-1.5 flex-1">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/55 shrink-0" />
                  <div className="h-1.5 w-full rounded-sm bg-muted-foreground/20" />
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0" />
                  <div className="h-1.5 w-3/4 rounded-sm bg-muted-foreground/18" />
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/25 shrink-0" />
                  <div className="h-1.5 w-2/3 rounded-sm bg-muted-foreground/15" />
                </div>
              </div>
            </div>
            </div>
          </div>

          <h3 className="text-base font-semibold">Slide Deck</h3>
          <p className="text-sm text-foreground/70 text-center mt-1">
            Presentation format for meetings and reviews
          </p>
        </button>
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleContinue}
          className="inline-flex items-center px-8 py-2.5 rounded-xl bg-primary text-white font-medium text-[15px] cursor-pointer shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all"
          data-testid="format-continue-button"
        >
          Continue
          <ArrowRight size={16} className="ml-2" />
        </button>
      </div>
    </div>
  );
}
